"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { fetchHeaderMenu } from "@/api-service/menu";
import Loader from "../common/LoaderComponent";

type MenuChild = {
  name: string;
  slug: string;
  type: number;
  children?: MenuChild[];
  categoryId?: number;
};

type MenuData = {
  id: number;
  name: string;
  data: MenuChild[];
  type: number;
};

type MenuResponse = {
  status: string;
  message: string;
  data: MenuData;
  meta: {
    timestamp: string;
  };
};

interface MenuItem {
  label: string;
  href: string;
}

interface MenuChildrenGroup {
  title: string;
  items: MenuItem[];
}

interface Menu {
  label: string;
  href: string;
  status?: number;
  children?: MenuChildrenGroup[];
}

export default function MenuHeader() {
  const [categories, setCategories] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMenu = async () => {
      try {
        const response: MenuResponse = await fetchHeaderMenu();
        const transformed = transformMenuData(response.data);
        setCategories(transformed);
      } catch (error) {
        console.error("Failed to load menu", error);
      } finally {
        setLoading(false);
      }
    };

    loadMenu();
  }, []);

  if (loading) {
    return (
      <div className="hidden md:block w-full bg-white border-t border-gray-100 shadow-sm h-[50px]">
        <div className="max-w-7xl mx-auto flex gap-6 py-3">
          <Loader variant="dots" label="Loading menus..." size="lg" />
        </div>
      </div>
    );
  }

  const activeCategories = categories.filter((cat) => cat.status !== 0);

  return (
    <div className="hidden md:block w-full font-normal text-[14px] bg-white border-t border-gray-100 shadow-sm h-[50px]">
      <div className="max-w-7xl mx-auto flex gap-6 py-3 relative">
        {activeCategories.map((category, index) => {
          const hasChildren = category.children && category.children.length > 0;

          return (
            <div key={index} className="group relative">
              <Link
                href={category.href}
                className="flex items-center gap-2 px-3 py-1 text-sm capitalize text-primary hover:text-primary"
              >
                <span>{category.label}</span>
              </Link>

              {hasChildren && (
                <div
                  className="absolute left-0 top-full mt-0 hidden group-hover:flex hover:flex bg-white shadow-xl rounded-lg p-6 z-50 gap-x-6 items-stretch"
                  style={{
                    width: "auto",
                    maxWidth: "880px",
                    flexWrap: "nowrap",
                  }}
                >
                  {(() => {
                    const columns: MenuChildrenGroup[][] = [];
                    let currentColumn: MenuChildrenGroup[] = [];
                    let currentCount = 0;

                    category.children!.forEach((group) => {
                      const groupItemCount = group.items.length;

                      if (currentCount + groupItemCount > 10) {
                        if (currentColumn.length > 0) {
                          columns.push(currentColumn);
                        }
                        currentColumn = [];
                        currentCount = 0;
                      }

                      currentColumn.push(group);
                      currentCount += groupItemCount;
                    });

                    if (currentColumn.length > 0) {
                      columns.push(currentColumn);
                    }

                    return columns.map((colGroups, colIdx) => {
                      const isLast = colIdx === columns.length - 1;
                      const addBorder = columns.length > 1 && !isLast;

                      return (
                        <div
                          key={colIdx}
                          className={`flex flex-col justify-start pr-6 ${
                            addBorder ? "border-r border-gray-200" : ""
                          }`}
                          style={{ minWidth: "200px" }}
                        >
                          {colGroups.map((group, i) => (
                            <div
                              key={`${category.label}-${colIdx}-${i}-${group.title}`}
                              className="mb-4"
                            >
                              <p className="text-sm font-medium text-gray-900 mb-2">
                                {group.title}
                              </p>
                              <ul className="space-y-2">
                                {group.items.map((item, itemIndex) => (
                                  <li
                                    key={`${category.label}-${colIdx}-${i}-${item.label}-${itemIndex}`}
                                  >
                                    <Link
                                      href={item.href}
                                      className="text-gray-500 hover:text-gray-600 text-sm block"
                                    >
                                      {item.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          );
        })}


        {/* Static Deals & Offers Link */}
        <div className="group relative">
          <Link
            href="/crazy-deals"
            className="flex items-center gap-1.5 px-3 py-1 text-sm font-semibold capitalize text-primary hover:text-[var(--brand-accent)] transition-colors"
          >
            <span>Deals &amp; Offers</span>
          </Link>
        </div>

      </div>
    </div>
  );
}


function transformMenuData(menuData: MenuData): Menu[] {
  // helper to get the correct URL based on type
  const getHref = (type: number, slug: string) => {
    switch (type) {
      case 1:
        return `/pages/${slug}`;
      case 2:
        return `/collections/${slug}`;
      case 3:
        return `/${slug}`;
      default:
        return `/${slug}`;
    }
  };

  return menuData.data.map((topCategory) => ({
    label: topCategory.name,
    href: getHref(topCategory.type, topCategory.slug),
    status: 1,
    children: topCategory.children
      ? topCategory.children.map((child) => {
          if (child.children && child.children.length > 0) {
            // has sub-children
            return {
              title: child.name,
              items: child.children.map((grandchild) => ({
                label: grandchild.name,
                href: getHref(grandchild.type, grandchild.slug),
              })),
            };
          }

          // single child item
          return {
            title: "",
            items: [
              {
                label: child.name,
                href: getHref(child.type, child.slug),
              },
            ],
          };
        })
      : undefined,
  }));
}
